import React, { useEffect, useMemo, useState } from "react";
import { Check, LoaderCircle, Search, Sparkles } from "lucide-react";
import { apiFetch } from "../../commons/axiosReq";
import "./CollegeImporter.css";
import "./AcademicStructureImporter.css";

const universityValue = (item) => item?.universityId ?? item?.value;
const universityLabel = (item) => item?.name ?? item?.label;
const normalizeName = (value) => (value || "").trim().replace(/\s+/g, " ").toLocaleLowerCase();

export default function CollegeImporter({ universities = [], initialUniversityId = null, onChanged }) {
  const [universityId, setUniversityId] = useState(initialUniversityId ? String(initialUniversityId) : "");
  const [maxFields, setMaxFields] = useState(50);
  const [suggestions, setSuggestions] = useState([]);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [savingId, setSavingId] = useState("");
  const [bulkProgress, setBulkProgress] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  useEffect(() => {
    setUniversityId(initialUniversityId ? String(initialUniversityId) : "");
    setSuggestions([]);
    setEdits({});
    setMessage("");
  }, [initialUniversityId]);

  const selectedUniversityName = useMemo(() => {
    const selected = universities.find((item) => String(universityValue(item)) === String(universityId));
    return universityLabel(selected) || "";
  }, [universities, universityId]);

  const findColleges = async () => {
    if (!universityId) {
      setMessageType("error");
      return setMessage("ابتدا دانشگاه را انتخاب کنید.");
    }
    setBusy(true);
    setMessage("");
    setSuggestions([]);
    const [result, collegesResult] = await Promise.all([
      apiFetch("AcademicStructureImport/OpenAlex/Preview", "post", {
        universityId: Number(universityId), maxFields: Number(maxFields)
      }),
      apiFetch("College/GetColleges", "get")
    ]);
    setBusy(false);
    if (!result.ok || !collegesResult.ok) {
      setMessageType("error");
      return setMessage(result.error || collegesResult.error || "جستجو یا بررسی دانشکده‌های موجود انجام نشد.");
    }
    const existingNames = new Set((Array.isArray(collegesResult.data) ? collegesResult.data : [])
      .filter((item) => String(item.universityId) === String(universityId))
      .map((item) => normalizeName(item.name)));
    const rawItems = Array.isArray(result.data) ? result.data : [];
    const items = Array.from(rawItems.reduce((map, item) => {
      // OpenAlex domains are limited to four global buckets, so using
      // collegeSuggestion here produced the same four cards for every
      // university. Its fields are institution-scoped and ranked by that
      // university's works, making them the useful editable college guesses.
      const name = (item.fieldName || item.collegeSuggestion || "").trim();
      const key = normalizeName(name);
      if (!name || existingNames.has(key)) return map;
      const current = map.get(key);
      map.set(key, current
        ? { ...current, worksCount: Number(current.worksCount || 0) + Number(item.worksCount || 0) }
        : { collegeKey: key, collegeName: name, worksCount: Number(item.worksCount || 0) });
      return map;
    }, new Map()).values());
    setSuggestions(items);
    setEdits(Object.fromEntries(items.map((item) => [item.collegeKey, { collegeName: item.collegeName }])));
    setMessageType(items.length ? "success" : "info");
    setMessage(items.length
      ? `${items.length.toLocaleString("fa-IR")} پیشنهاد برای ${selectedUniversityName || "دانشگاه انتخاب‌شده"} پیدا شد.`
      : "پیشنهاد جدیدی پیدا نشد؛ موارد موجود از فهرست حذف شدند.");
  };

  const updateEdit = (id, key, value) => setEdits((current) => ({
    ...current, [id]: { ...current[id], [key]: value }
  }));

  const importSuggestion = async (suggestion) => {
    const values = edits[suggestion.collegeKey];
    if (!values?.collegeName?.trim()) {
      setMessageType("error");
      return setMessage("نام دانشکده را کامل کنید.");
    }
    setSavingId(suggestion.collegeKey);
    setMessage("");
    const result = await apiFetch("AcademicStructureImport/OpenAlex/ApproveCollege", "post", {
      universityId: Number(universityId), collegeName: values.collegeName.trim()
    });
    setSavingId("");
    if (!result.ok) {
      setMessageType("error");
      return setMessage(result.error || "وارد کردن دانشکده انجام نشد.");
    }
    setSuggestions((current) => current.filter((item) => item.collegeKey !== suggestion.collegeKey));
    setMessageType("success");
    setMessage(result.data?.collegeCreated ? "دانشکده با موفقیت وارد شد." : "این دانشکده از قبل ثبت شده بود.");
    if (onChanged) onChanged();
  };

  const importAllSuggestions = async () => {
    const invalidItems = suggestions.filter((item) => !edits[item.collegeKey]?.collegeName?.trim());
    if (invalidItems.length) {
      setMessageType("error");
      return setMessage(`نام ${invalidItems.length.toLocaleString("fa-IR")} دانشکده خالی است؛ ابتدا آن‌ها را کامل کنید.`);
    }
    const editedNames = suggestions.map((item) => normalizeName(edits[item.collegeKey].collegeName));
    if (new Set(editedNames).size !== editedNames.length) {
      setMessageType("error");
      return setMessage("نام‌های تکراری در پیشنهادها وجود دارد؛ قبل از ورود همه آن‌ها را اصلاح کنید.");
    }

    const importedIds = new Set();
    const errors = [];
    setSavingId("__all__");
    setBulkProgress({ done: 0, total: suggestions.length });
    setMessage("");

    for (let offset = 0; offset < suggestions.length; offset += 5) {
      const batch = suggestions.slice(offset, offset + 5);
      const results = await Promise.all(batch.map(async (suggestion) => ({
        suggestion,
        result: await apiFetch("AcademicStructureImport/OpenAlex/ApproveCollege", "post", {
          universityId: Number(universityId),
          collegeName: edits[suggestion.collegeKey].collegeName.trim()
        })
      })));
      results.forEach(({ suggestion, result }) => {
        if (result.ok) importedIds.add(suggestion.collegeKey);
        else errors.push(result.error || `ورود ${edits[suggestion.collegeKey].collegeName} انجام نشد.`);
      });
      setBulkProgress({ done: Math.min(offset + batch.length, suggestions.length), total: suggestions.length });
    }

    setSuggestions((current) => current.filter((item) => !importedIds.has(item.collegeKey)));
    setSavingId("");
    setBulkProgress(null);
    if (importedIds.size && onChanged) onChanged();

    if (!errors.length) {
      setMessageType("success");
      setMessage(`${importedIds.size.toLocaleString("fa-IR")} دانشکده با موفقیت تأیید و وارد شد.`);
    } else {
      setMessageType("error");
      setMessage(`${importedIds.size.toLocaleString("fa-IR")} دانشکده وارد شد و ${errors.length.toLocaleString("fa-IR")} مورد انجام نشد. ${errors[0]}`);
    }
  };

  return <div className="college-importer">
    <div className="college-importer-intro"><span><Sparkles size={20} /></span><div><strong>پیدا کردن دانشکده‌ها با OpenAlex</strong><p>پیشنهادها از حوزه‌های پژوهشی پرتکرار همان دانشگاه ساخته می‌شوند؛ نام رسمی دانشکده را پیش از ورود بررسی و اصلاح کنید.</p></div></div>
    <div className="college-importer-controls">
      <label>دانشگاه<select value={universityId} disabled={Boolean(initialUniversityId) || busy || Boolean(savingId)} onChange={(event) => { setUniversityId(event.target.value); setSuggestions([]); setMessage(""); }}><option value="">انتخاب دانشگاه</option>{universities.map((item) => <option key={universityValue(item)} value={universityValue(item)}>{universityLabel(item)}</option>)}</select></label>
      <label>تعداد پیشنهاد<select value={maxFields} disabled={busy || Boolean(savingId)} onChange={(event) => setMaxFields(Number(event.target.value))}><option value={12}>۱۲ مورد</option><option value={30}>۳۰ مورد</option><option value={50}>۵۰ مورد</option><option value={100}>۱۰۰ مورد</option><option value={200}>۲۰۰ مورد</option></select></label>
      <button type="button" disabled={busy || Boolean(savingId) || !universityId} onClick={findColleges}>{busy ? <LoaderCircle className="college-importer-spinner" size={18} /> : <Search size={18} />}{busy ? "در حال جستجو..." : "پیدا کردن دانشکده‌ها"}</button>
    </div>
    {message && <div className={`college-importer-message is-${messageType}`}>{message}</div>}
    {suggestions.length > 0 && <div className="college-importer-results">
      <div className="college-importer-results-head"><strong>پیشنهادهای پیدا‌شده</strong><div className="college-importer-results-head-actions"><span>{suggestions.length.toLocaleString("fa-IR")} مورد باقی مانده</span><button type="button" disabled={Boolean(savingId)} onClick={importAllSuggestions}>{savingId === "__all__" ? <LoaderCircle className="college-importer-spinner" size={16} /> : <Check size={16} />}{bulkProgress ? `در حال ورود ${bulkProgress.done.toLocaleString("fa-IR")} از ${bulkProgress.total.toLocaleString("fa-IR")}` : "تأیید و ورود همه"}</button></div></div>
      <div className="college-importer-list">{suggestions.map((suggestion) => {
        const values = edits[suggestion.collegeKey] || {};
        const saving = savingId === suggestion.collegeKey;
        return <article key={suggestion.collegeKey} className="college-importer-card is-single-field">
          <div className="college-importer-card-meta"><span>{Number(suggestion.worksCount || 0).toLocaleString("fa-IR")} اثر پژوهشی مرتبط</span></div>
          <label>نام دانشکده<input disabled={Boolean(savingId)} value={values.collegeName || ""} onChange={(event) => updateEdit(suggestion.collegeKey, "collegeName", event.target.value)} /></label>
          <button type="button" disabled={saving || Boolean(savingId)} onClick={() => importSuggestion(suggestion)}>{saving ? <LoaderCircle className="college-importer-spinner" size={17} /> : <Check size={17} />}{saving ? "در حال ورود..." : "تأیید و ورود"}</button>
        </article>;
      })}</div>
    </div>}
  </div>;
}
